/**
 * @file anonymous.ts
 *
 * @fileoverview This HOC will allow an anonymous user to access a specific page.
 *  If the user is not anonymous, it will make an appropriate redirect.
 */
import { GetServerSidePropsContext } from 'next';
import { WithAuthenticateProps } from '~/jobiqo/hocs/withAuthenticate';
import { isAuthenticated } from '~/jobiqo/lib/auth';
import { gsspRedirect, sanitizeRedirectUrl } from '~/jobiqo/lib/redirect';

export interface WithAuthenticateAnonymousProps extends WithAuthenticateProps {
  /**
   * A custom place to redirect to if the user is not anonymous.
   *
   * @default "/dashboard"
   */
  redirectTo?: string;
}

/**
 * Higher order getServerSideProps function that can be called from inside a page.
 *
 * @param {GetServerSideProps} gssp
 *  The getServerSideProps function from the page that calls this function.
 * @param {string} redirectTo
 *  The redirect route.
 * @returns
 *  Called gssp function or appropriate getServerSideProps return value.
 */
export const withAuthenticateAnonymous = ({
  gssp,
  redirectTo = '/dashboard',
}: WithAuthenticateAnonymousProps = {}) => {
  return async (ctx: GetServerSidePropsContext) => {
    const isAuth = await isAuthenticated(ctx);

    if (isAuth) {
      // If there is a destination parameter, redirect to it.
      // Prioritize destination parameter over redirectTo.
      const destination = ctx.query.destination as string | undefined;
      if (destination) {
        return {
          redirect: {
            destination: gsspRedirect(
              ctx,
              sanitizeRedirectUrl(destination, redirectTo),
            ),
            permanent: false,
          },
        };
      }
      // If user is authenticated redirect to dashboard.
      return {
        redirect: {
          destination: gsspRedirect(ctx, redirectTo),
          permanent: false,
        },
        props: {},
      };
    }

    if (gssp) {
      return await gssp(ctx);
    }

    return {
      props: {},
    };
  };
};
